/**
 * TaskFilterToolbar (Story 28.2) — a clone of the alerts AlertFilterToolbar
 * (search Input with clear X + ViewSwitcher + brand Create button) RE-WIDENED
 * with the clients ClientFilterToolbar multiselect filter chrome: a Funnel
 * popover with tabbed multiselect (Status / Assignee / Client / Tags) and a
 * row of active filter chips below. Chrome (radii, borders, shadows, motion) is
 * byte-for-byte the clients/alerts toolbars per the design-system directive
 * §1/§2/§9.
 *
 * NO react-query — this is a pure leaf rendered bare in tests (no QueryClient).
 */

import { useState } from "react";
import { Input, Button, cn } from "@provenleap/ui";
import {
  MagnifyingGlass,
  X,
  Funnel,
  Check,
  Kanban,
  CaretDown,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { ViewSwitcher, type TaskViewMode } from "./ViewSwitcher";
import { STATUS_OPTIONS, statusLabel } from "./taskDisplay";
import type { TaskStatus } from "./types";
import { TagPill } from "../clients/TagPill";
import { TASK_HIDEABLE_COLUMNS } from "./taskColumns";
import { ColumnVisibilityMenu } from "../shared/columnVisibility";

type FilterTab = "status" | "assignee" | "client" | "tags";

interface TaskFilterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  viewMode: TaskViewMode;
  onViewModeChange: (mode: TaskViewMode) => void;
  statusFilter: TaskStatus[];
  onStatusFilterChange: (statuses: TaskStatus[]) => void;
  assigneeFilter: string[];
  onAssigneeFilterChange: (assignees: string[]) => void;
  clientFilter: string[];
  onClientFilterChange: (clients: string[]) => void;
  tagFilter: string[];
  onTagFilterChange: (tagIds: string[]) => void;
  assigneeOptions: { id: string; name: string }[];
  clientOptions: { id: string; name: string }[];
  tagOptions: { id: string; name: string; color: string }[];
  canManage?: boolean;
  onCreate?: () => void;
  /** Column-visibility (table view only). Hidden column ids + toggle handler. */
  hiddenColumns?: string[];
  onToggleColumn?: (id: string) => void;
  /** Show the "Columns" control — only relevant in the table view. */
  showColumnsControl?: boolean;
}

export function TaskFilterToolbar({
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  statusFilter,
  onStatusFilterChange,
  assigneeFilter,
  onAssigneeFilterChange,
  clientFilter,
  onClientFilterChange,
  tagFilter,
  onTagFilterChange,
  assigneeOptions,
  clientOptions,
  tagOptions,
  canManage = false,
  onCreate,
  hiddenColumns = [],
  onToggleColumn,
  showColumnsControl = false,
}: TaskFilterToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("status");
  const [filterSearch, setFilterSearch] = useState("");

  const totalFilterCount =
    statusFilter.length +
    assigneeFilter.length +
    clientFilter.length +
    tagFilter.length;

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "status", label: "Status", count: statusFilter.length },
    { key: "assignee", label: "Assignee", count: assigneeFilter.length },
    { key: "client", label: "Client", count: clientFilter.length },
    { key: "tags", label: "Tags", count: tagFilter.length },
  ];

  const toggle = <T,>(list: T[], value: T, onChange: (next: T[]) => void) => {
    onChange(
      list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value],
    );
  };

  const clearFilters = () => {
    onStatusFilterChange([]);
    onAssigneeFilterChange([]);
    onClientFilterChange([]);
    onTagFilterChange([]);
  };

  const tabSearchNoun =
    activeTab === "status"
      ? "statuses"
      : activeTab === "assignee"
        ? "assignees"
        : activeTab === "client"
          ? "clients"
          : "tags";

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: Search + Filter + View + Show done */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground"
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search tasks..."
            aria-label="Search tasks"
            className="pl-9 pr-9 rounded-2xl text-foreground placeholder:text-foreground shadow-xs text-[13.5px]"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Columns dropdown — table view only (show/hide table columns) */}
        {showColumnsControl && onToggleColumn && (
          <ColumnVisibilityMenu
            columns={TASK_HIDEABLE_COLUMNS}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggleColumn}
          />
        )}

        {/* Filters dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => {
              setFilterOpen(!filterOpen);
              setFilterSearch("");
            }}
          >
            <Funnel size={16} className="mr-1" />
            Filters
            {totalFilterCount > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
            <CaretDown size={12} className="ml-1" />
          </Button>

          <AnimatePresence>
            {filterOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setFilterOpen(false);
                    setFilterSearch("");
                  }}
                />

                {/* Dropdown panel */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute left-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                  style={{ border: "0.5px solid var(--hairline)" }}
                >
                  {/* Arrow pointing up */}
                  <div
                    className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                    style={{
                      borderLeft: "0.5px solid var(--hairline)",
                      borderTop: "0.5px solid var(--hairline)",
                    }}
                  />
                  {/* Tab bar */}
                  <div className="flex mx-4 pb-0">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.key);
                          setFilterSearch("");
                        }}
                        className={cn(
                          "flex-1 px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer",
                          activeTab === tab.key
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Search */}
                  <div className="px-2 pt-2 pb-0.5">
                    <div className="relative">
                      <MagnifyingGlass
                        size={16}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="text"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder={`Search ${tabSearchNoun}...`}
                        className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                        style={{ outline: "none", boxShadow: "none" }}
                      />
                    </div>
                  </div>

                  {/* Tab content */}
                  <div className="max-h-[260px] overflow-y-auto pt-2 pb-1">
                    {/* Status tab */}
                    {activeTab === "status" && (
                      <>
                        {STATUS_OPTIONS.filter(
                          (o) =>
                            !filterSearch ||
                            o.label
                              .toLowerCase()
                              .includes(filterSearch.toLowerCase()),
                        ).map((option) => {
                          const isSelected = statusFilter.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                toggle(
                                  statusFilter,
                                  option.value,
                                  onStatusFilterChange,
                                )
                              }
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected &&
                                    "bg-primary border-primary text-white",
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              {option.label}
                            </button>
                          );
                        })}
                      </>
                    )}

                    {/* Assignee tab */}
                    {activeTab === "assignee" && (
                      <>
                        {assigneeOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No assignees yet
                          </div>
                        ) : (
                          [...assigneeOptions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(
                              (a) =>
                                !filterSearch ||
                                a.name
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase()),
                            )
                            .map((assignee) => {
                              const isSelected = assigneeFilter.includes(
                                assignee.id,
                              );
                              return (
                                <button
                                  key={assignee.id}
                                  type="button"
                                  onClick={() =>
                                    toggle(
                                      assigneeFilter,
                                      assignee.id,
                                      onAssigneeFilterChange,
                                    )
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white",
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  {assignee.name}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}

                    {/* Client tab */}
                    {activeTab === "client" && (
                      <>
                        {clientOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No clients yet
                          </div>
                        ) : (
                          [...clientOptions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(
                              (c) =>
                                !filterSearch ||
                                c.name
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase()),
                            )
                            .map((client) => {
                              const isSelected = clientFilter.includes(
                                client.id,
                              );
                              return (
                                <button
                                  key={client.id}
                                  type="button"
                                  onClick={() =>
                                    toggle(
                                      clientFilter,
                                      client.id,
                                      onClientFilterChange,
                                    )
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white",
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  {client.name}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}

                    {/* Tags tab */}
                    {activeTab === "tags" && (
                      <>
                        {tagOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No tags yet
                          </div>
                        ) : (
                          [...tagOptions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(
                              (t) =>
                                !filterSearch ||
                                t.name
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase()),
                            )
                            .map((tag) => {
                              const isSelected = tagFilter.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() =>
                                    toggle(tagFilter, tag.id, onTagFilterChange)
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white",
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  <span
                                    className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                                    style={{ backgroundColor: tag.color }}
                                  />
                                  {tag.name}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* View switcher + Create — pushed to the right */}
        <div className="flex gap-4 sm:ml-auto">
          {/* View switcher */}
          <ViewSwitcher view={viewMode} onViewChange={onViewModeChange} />

          {canManage && onCreate && (
            <Button
              variant="brand"
              className="shrink-0 rounded-2xl text-[13px] cursor-pointer"
              onClick={onCreate}
            >
              <Kanban size={16} className="mr-1" />
              Create Task
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: Active filter chips */}
      {totalFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Status chips */}
          {statusFilter.map((status) => (
            <span
              key={`status-${status}`}
              className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
            >
              {statusLabel(status)}
              <button
                type="button"
                onClick={() =>
                  toggle(statusFilter, status, onStatusFilterChange)
                }
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          {/* Assignee chips */}
          {assigneeFilter.map((id) => {
            const name =
              assigneeOptions.find((a) => a.id === id)?.name ?? id;
            return (
              <span
                key={`assignee-${id}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                {name}
                <button
                  type="button"
                  onClick={() =>
                    toggle(assigneeFilter, id, onAssigneeFilterChange)
                  }
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          {/* Client chips */}
          {clientFilter.map((id) => {
            const name = clientOptions.find((c) => c.id === id)?.name ?? id;
            return (
              <span
                key={`client-${id}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                {name}
                <button
                  type="button"
                  onClick={() => toggle(clientFilter, id, onClientFilterChange)}
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          {/* Tag chips — solid colored TagPills (matches the clients filter). */}
          {tagFilter.map((tagId) => {
            const tag = tagOptions.find((t) => t.id === tagId);
            if (!tag) return null;
            return (
              <TagPill
                key={`tag-${tagId}`}
                tag={tag}
                size="sm"
                className="!text-[13px]"
                onRemove={() => toggle(tagFilter, tagId, onTagFilterChange)}
              />
            );
          })}
          <button
            type="button"
            onClick={clearFilters}
            className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            style={{ color: "#5A7396" }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
